import type { AuditEntryDto } from '@gsls/shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  ErrorNote,
  Field,
  PageHeader,
  Spinner,
  inputClass,
} from '../components/ui';
import { DataTable, type DataColumn } from '../components/DataTable';
import { FilterBar } from '../components/FilterBar';
import { api } from '../lib/api';
import { formatDateTime } from '../lib/format';

/**
 * The audit trail.
 *
 * Every mutation in the app already writes a row here in the same transaction as
 * the change. This is the first place those rows are readable.
 */
export function AuditPage() {
  const [action, setAction] = useState('');
  const [entityType, setEntityType] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);

  const actions = useQuery({
    queryKey: ['audit', 'actions'],
    queryFn: api.audit.actions,
  });

  const entries = useQuery({
    queryKey: ['audit', { action, entityType }],
    queryFn: () =>
      api.audit.list({
        ...(action ? { action } : {}),
        ...(entityType ? { entityType } : {}),
        limit: 100,
      }),
  });

  const rows = entries.data?.items ?? [];

  const auditColumns: DataColumn<AuditEntryDto>[] = [
    {
      key: 'when',
      header: 'When',
      align: 'right',
      cell: (entry) => (
        <span className="whitespace-nowrap text-xs">{formatDateTime(entry.createdAt)}</span>
      ),
    },
    {
      key: 'who',
      header: 'Who',
      cell: (entry) => (
        <span className="text-xs">
          {entry.actorName ?? 'system'}
          {entry.ip ? (
            <>
              <br />
              <span className="text-ink-muted">{entry.ip}</span>
            </>
          ) : null}
        </span>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      primary: true,
      cell: (entry) => (
        <Badge tone={entry.action.endsWith('create') ? 'green' : 'accent'}>{entry.action}</Badge>
      ),
    },
    {
      key: 'entity',
      header: 'Entity',
      cell: (entry) => (
        <span className="text-xs">
          {entry.entityType}
          {entry.entityId ? (
            <>
              <br />
              <span className="font-mono text-ink-muted">{entry.entityId.slice(0, 8)}…</span>
            </>
          ) : null}
        </span>
      ),
    },
  ];

  const auditDetail = (entry: AuditEntryDto) =>
    expanded === entry.id ? (
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-ink-muted">Before</p>
          <pre className="mt-1 overflow-x-auto rounded bg-white p-3 font-mono text-xs text-ink-body">
            {entry.before ? JSON.stringify(entry.before, null, 2) : '—'}
          </pre>
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-ink-muted">After</p>
          <pre className="mt-1 overflow-x-auto rounded bg-white p-3 font-mono text-xs text-ink-body">
            {entry.after ? JSON.stringify(entry.after, null, 2) : '—'}
          </pre>
        </div>
      </div>
    ) : null;

  return (
    <>
      <PageHeader
        eyebrow="Administration"
        title="Audit trail"
        description="Who changed what, when, and what it looked like before. Each entry is written in the same transaction as the change it describes, so the trail cannot describe something that did not happen."
      />

      <Card className="mb-4">
        <CardBody>
          <FilterBar activeCount={(action ? 1 : 0) + (entityType ? 1 : 0)}>
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="Action" hint="Contains match">
                <select
                  name="auditAction"
                  className={inputClass}
                  value={action}
                  onChange={(event) => setAction(event.target.value)}
                >
                  <option value="">Any action</option>
                  {(actions.data?.actions ?? []).map((candidate) => (
                    <option key={candidate} value={candidate}>
                      {candidate}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Entity type" hint="lot, batch, item, user…">
                <input
                  name="auditEntityType"
                  className={inputClass}
                  value={entityType}
                  onChange={(event) => setEntityType(event.target.value)}
                  placeholder="movement"
                />
              </Field>

              <div className="flex items-end">
                <Button
                  tone="ghost"
                  onClick={() => {
                    setAction('');
                    setEntityType('');
                  }}
                >
                  Clear filters
                </Button>
              </div>
            </div>
          </FilterBar>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          {entries.isPending ? <Spinner label="Reading the trail…" /> : null}
          <ErrorNote>
            {entries.isError
              ? 'Could not read the audit trail. It needs the audit.read capability.'
              : ''}
          </ErrorNote>

          {entries.isSuccess ? (
            <>
              <div className="mb-4 text-sm text-ink-muted">
                {entries.data.total} entr{entries.data.total === 1 ? 'y' : 'ies'}
              </div>
              <DataTable
                rows={rows}
                columns={auditColumns}
                rowKey={(entry) => entry.id}
                actionsHeader={undefined}
                actions={(entry) => (
                  <Button
                    tone="ghost"
                    onClick={() => setExpanded(expanded === entry.id ? null : entry.id)}
                  >
                    {expanded === entry.id ? 'Hide' : 'Details'}
                  </Button>
                )}
                detail={auditDetail}
                empty="Nothing recorded for those filters."
              />
            </>
          ) : null}
        </CardBody>
      </Card>
    </>
  );
}
